import { ArrowDownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  ActivatingEvent,
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
  dbCols,
} from '@cc/schema';
import { Create } from '@refinedev/antd';

import {
  Col,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Slider,
  Tooltip,
  Typography,
} from 'antd';

import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { useActor } from '@xstate/react';
import { useWatch } from 'antd/lib/form/Form';
import qs from 'qs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TherapistActivityReactContext } from '../../App';
import { useClientOptions } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { AbcModel } from '../../schema/be-graphql-generated';
import { colors } from '../../styles/colors';
import useStyles from './styles.style';
import { useLocation } from 'react-router-dom';
import { SelectClient } from '../../components/SelectClient';

const { Title } = Typography;

const { OptGroup, Option } = Select;

export const AbcModelCreate = () => {
  const { t, i18n } = useTranslation();

  const location = useLocation();

  const { styles } = useStyles();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const baseEmotions = useMemo(() => {
    const baseEmotions = currentLocale?.startsWith('ru')
      ? baseEmotionsRu
      : baseEmotionsEn;

    return baseEmotions.map((e) => ({
      ...e,
      children: e.children.map((ch) => ({ ...ch, strength: 50 })),
    }));
  }, [currentLocale]);

  const flattenedEmotions = useMemo(() => {
    return baseEmotions.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseEmotionsEn)[0]['children'],
    );
  }, [baseEmotions]);

  const baseFeelings = currentLocale?.startsWith('ru')
    ? baseFeelingsRu
    : baseFeelingsEn;

  const { clientsLoading } = useClientOptions();

  const { clientId: locationClientId, abcModelId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  const initialValues = {
    a: '' as string | undefined | null,
    b: '' as string | undefined | null,
    behaviors: '' as string | undefined | null,
    emotions: [] as ActivatingEvent['emotions'] | null[],
    feelings: [] as ActivatingEvent['feelings'] | null[],
  };

  const [form] = Form.useForm<typeof initialValues>();

  // Refine's unsaved-changes contract: setting `warnWhen` makes the router (and the sider's
  // logout) confirm before leaving, using the existing `warnWhenUnsavedChanges` copy.
  // `UnsavedChangesNotifier` is mounted in App.tsx — without it only the sider honoured this.
  const { setWarnWhen } = useWarnAboutChange();
  /** Unsaved work exists — drives BOTH the leave-warning and the Save button's enabled state. */
  const [dirty, setDirty] = useState(false);

  const emotions = useWatch('emotions', form);

  const [modelId, setModelId] = useState(abcModelId as string | undefined);

  useActivityTracking(modelId ? modelId : `${dbCols.abcModels}/create`);

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const {
    data: abcModelData,
    isLoading: abcModelLoading,
    refetch: refetchAbcModel,
    error: abcModelError,
  } = useCustom<AbcModel>({
    url: '',
    method: 'get',
    meta: {
      operation: 'abcModel',
      fields: [
        '_id',
        'a',
        'b',
        'behaviors',
        'feelings',
        { emotions: ['id', 'strength'] },
        'userId',
      ],

      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: modelId,
        },
      },
    },
  });

  useEffect(() => {
    if (abcModelData?.data) {
      form?.setFieldsValue({
        a: abcModelData?.data?.a,
        b: abcModelData?.data?.b,
        emotions: abcModelData?.data?.emotions || [],
        behaviors: abcModelData?.data?.behaviors || '',
        feelings: abcModelData?.data?.feelings || [],
      });
      setDirty(false); // loading is not editing
      setClientId(abcModelData?.data.userId);
      if (abcModelData?.data?._id) {
        therapistActivitySend({
          type: 'LOADED',
          resource: abcModelData?.data?._id,
          clientId: abcModelData?.data?.userId,
        });
      }
    }
  }, [abcModelData]);

  const {
    mutateAsync: upsertAbcModel,
    data: upsertAbcModelData,
    isLoading: upsertAbcModelLoading,
  } = useCustomMutation<{ abcModel: AbcModel }>();

  /**
   * `mutateAsync`, not `mutate`: with explicit save the caller MUST know whether the write landed.
   * `mutate` is fire-and-forget — awaiting it does nothing — so the old code announced success
   * unconditionally, and now that a save also clears the unsaved-work guard, a failed one would
   * quietly drop the therapist's protection while telling them it had saved.
   */
  const saveData = useCallback(
    async (data: typeof initialValues) => {
      if (!clientId) {
        return;
      }
      await upsertAbcModel({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertAbcModel',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'AbcModelInput',
              value: {
                data,
                where: {
                  clientId,
                  _id: modelId,
                },
              },
            },
          },
        },
      });
      message.success(t('shared.abcModel.saveSuccess'));
    },
    [clientId, modelId, t],
  );

  useEffect(() => {
    // @ts-ignore
    if (upsertAbcModelData?.data?._id) {
      if (!modelId) {
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: upsertAbcModelData?.data?._id,
        });

        // @ts-ignore
        setModelId(upsertAbcModelData?.data?._id);
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          resource: modelId,
        });
      }
    }
  }, [upsertAbcModelData]);

  /**
   * EXPLICIT SAVE (founder decision, 2026-09-01, applied per-tool as each epic lands). This form
   * used to autosave every change on a 700 ms debounce with the Save button hidden
   * (`saveButtonProps: { style: { display: 'none' } }`), which is wrong in three separate ways:
   *
   *   • it writes the therapist's keystrokes into the event log — one event per typing pause, each
   *     carrying the full clinical worksheet, so the stream records drafts instead of decisions;
   *   • `modelId` is only set when the FIRST save's response returns, so a second debounced save
   *     firing before it INSERTs a SECOND record for one worksheet (a legacy bug, not an ES one —
   *     the therapist branch inserts whenever `_id` is null; it is the same defect the founder hit
   *     by hand on the downward-arrow form);
   *   • there was no way for the therapist to say "I have finished thinking".
   *
   * A save is now a deliberate act, and leaving with unsaved work warns first.
   */
  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
  }, [setWarnWhen]);

  const onSave = useCallback(async () => {
    try {
      await saveData(form.getFieldsValue());
    } catch {
      // the work is still unsaved: keep the guard up and leave Save offered
      return;
    }
    setDirty(false);
    setWarnWhen(false); // saved — leaving is safe again
  }, [saveData, form, setWarnWhen]);

  return (
    <Create
      title={
        <>
          {t('shared.abcModel.title')}

          <Tooltip
            title={t('shared.abcModel.description')}
            overlayClassName={styles['abc-model--tooltip']}
          >
            <QuestionCircleOutlined style={{ margin: '0 10px' }} />
          </Tooltip>
        </>
      }
      isLoading={clientsLoading}
      saveButtonProps={{
        'onClick': onSave,
        'loading': upsertAbcModelLoading,
        // nothing to save is not a thing to offer: the button follows the dirty flag that also
        // drives the leave-warning, so "enabled" and "you would lose work" mean the same thing
        'disabled': !clientId || !dirty,
        'data-testid': 'cc-abc-model--save',
      }}
    >
      <div
        className={styles['abc-model-container']}
        data-testid="cc-create-abc-model-container"
      >
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>

      <Form<typeof initialValues>
        form={form}
        initialValues={{
          ...initialValues,
        }}
        onFinish={(values) => {}}
        onValuesChange={onValuesChange}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 16 }}
        labelWrap
      >
        <Card
          title={t('shared.abcModel.a')}
          // Nnote: using classnames does not work for some reason
          styles={{
            title: {
              display: 'flex',
              justifyContent: 'center',
              fontWeight: 'bold',
            },
          }}
        >
          <Form.Item name="a" className={styles['abc-model--row']}>
            <Input.TextArea
              placeholder={t('shared.abcModel.aPlaceholder')}
              autoSize={{ minRows: 4, maxRows: 10 }}
              data-testid="abc-model--input-a"
            />
          </Form.Item>
        </Card>
        <div className={styles['abc-models--question-container']}>
          <ArrowDownOutlined color={colors['--triad-blue-0']} />
        </div>

        <Card
          title={t('shared.abcModel.b')}
          styles={{
            title: {
              display: 'flex',
              justifyContent: 'center',
              fontWeight: 'bold',
            },
          }}
        >
          <Form.Item name="b" className={styles['abc-model--row']}>
            <Input.TextArea
              placeholder={t('shared.abcModel.bPlaceholder')}
              autoSize={{ minRows: 4, maxRows: 10 }}
              data-testid="abc-model--input-b"
            />
          </Form.Item>
        </Card>
        <div className={styles['abc-models--question-container']}>
          <ArrowDownOutlined color={colors['--triad-blue-0']} />
        </div>

        <Card
          title={t('shared.abcModel.c')}
          styles={{
            title: {
              display: 'flex',
              justifyContent: 'center',
              fontWeight: 'bold',
            },
          }}
        >
          <Form.Item
            name={['behaviors']}
            label={t('shared.abcModel.behaviors')}
          >
            <Input.TextArea
              placeholder={t('shared.abcModel.behaviorsPlaceholder')}
              autoSize={{ minRows: 4, maxRows: 8 }}
              data-testid="abc-model--input-c-behaviors"
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name={['emotions']}
            label={t('shared.abcModel.emotions')}
            getValueProps={(value) => {
              return value.id;
            }}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={t('shared.abcModel.emotionsPlaceholder')}
              optionFilterProp="children"
              value={emotions?.map((e: any) => e.id)}
              onChange={(emotionsIds) => {
                const eventEmotions =
                  (emotionsIds as unknown as number[]).map((id) => {
                    const emotion = flattenedEmotions.find((e) => e.id === id);
                    if (emotion) {
                      // @ts-ignore
                      const existingEmotion = emotions.find(
                        (em: any) => em.id === id,
                      );
                      return {
                        id: emotion.id,
                        strength: !!existingEmotion
                          ? existingEmotion.strength
                          : 50,
                      };
                    }
                  }) || [];

                form.setFieldValue(['emotions'], eventEmotions);
                // `setFieldValue` does not raise `onValuesChange`, so mark the form dirty by hand —
                // it used to force an autosave here instead
                onValuesChange();
              }}
              data-testid="abc-model--input-c-emotions"
            >
              {baseEmotions.map((eg) => {
                return (
                  <OptGroup label={eg.name} key={eg.name}>
                    {eg.children.map((e) => {
                      return (
                        <Option
                          key={e.id}
                          value={e.id}
                          data-testid="abc-model--input-c-emotion"
                        >
                          {e.name}
                        </Option>
                      );
                    })}
                  </OptGroup>
                );
              })}
            </Select>
          </Form.Item>

          {!!emotions?.length && (
            <Title level={5} style={{ textAlign: 'center' }}>
              {t('createThought.rateEmotions')}:
            </Title>
          )}

          <Form.List name={['emotions']}>
            {(fields, { add, remove }, errors) => {
              return (
                <>
                  {fields.map((field, index) => {
                    return (
                      <Row key={field.key}>
                        <Col span={13} push={2}>
                          <Form.Item
                            {...field}
                            label={
                              flattenedEmotions.find(
                                (e) => e.id === emotions?.[field.key]?.id,
                              )?.name || ''
                            }
                            key={field.key}
                            name={[field.key, 'strength']}
                          >
                            <Slider min={1} max={100} />
                          </Form.Item>
                        </Col>
                        <Col span={1} offset={2}>
                          <Form.Item
                            {...field}
                            key={field.key}
                            name={[field.key, 'strength']}
                          >
                            <InputNumber min={1} max={100} />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  })}
                </>
              );
            }}
          </Form.List>

          <Divider />

          <Form.Item
            label={t('shared.abcModel.sensations')}
            name={['feelings']}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder={t('shared.abcModel.sensationsPlaceholder')}
              data-testid="abc-model--input-c-sensations"
            >
              {baseFeelings.map((bg) => {
                return (
                  <OptGroup label={bg.name} key={bg.name}>
                    {bg.children.map((e) => {
                      return (
                        <Option
                          key={e.id}
                          value={e.id}
                          data-testid="abc-model--input-c-sensation"
                        >
                          {e.name}
                        </Option>
                      );
                    })}
                  </OptGroup>
                );
              })}
            </Select>
          </Form.Item>
        </Card>
      </Form>
    </Create>
  );
};
